import React, { useState, useEffect } from 'react';
import { Stack, TextField, Box, Typography, Alert } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import parse, { DOMNode } from 'html-react-parser';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';

import { VideoContentBlock } from '../../../_types/dtb';
import { BlockCommonProps } from './BlockSelector';
import BrightcovePlayer from '../../_components/BrightcovePlayer';
import { isValidBrightcoveUrl } from '../../_utils/brightcoveHelper';

const MAX_CAPTION = 300;

interface ContentBlock extends BlockCommonProps {
  block: VideoContentBlock;
}

const VideoBlock: React.FC<ContentBlock> = (props) => {
  const [titleValue, setTitleValue] = useState(props.block.title || '');
  const [videoUrlError, setVideoUrlError] = useState<string | null>(null);

  useEffect(() => {
    setTitleValue(props.block.title || '');
  }, [props.block.title]);

  // Validate video URL on mount and when it changes
  useEffect(() => {
    const url = props.block.contentData?.videoUrl;
    if (url && !isValidBrightcoveUrl(url)) {
      setVideoUrlError(t('dtbs.library.detail.content.videoBlock.videoUrlError'));
    } else {
      setVideoUrlError(null);
    }
  }, [props.block.contentData?.videoUrl]);

  const handleSave = ({
    url = props.block.contentData?.videoUrl ?? '',
    desc = props.block.contentData?.caption ?? '',
    title = titleValue,
  }: {
    url?: string;
    desc?: string;
    title?: string;
  }) => {
    const newVideoBlock: VideoContentBlock = {
      ...props.block,
      title: title,
      contentData: {
        videoUrl: url,
        caption: desc,
      },
    };
    props.onSave(props.index, newVideoBlock);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTitleValue(e.target.value);
  };

  const handleTitleBlur = () => {
    if (titleValue !== props.block.title) {
      handleSave({ title: titleValue });
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (
      e.target.value.length <= MAX_CAPTION ||
      e.target.value.length < (props.block.contentData?.caption?.length ?? MAX_CAPTION)
    ) {
      handleSave({ desc: e.target.value });
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newUrl = e.target.value;

    // Validate the URL
    if (newUrl && !isValidBrightcoveUrl(newUrl)) {
      setVideoUrlError(t('dtbs.library.detail.content.videoBlock.videoUrlError'));
    } else {
      setVideoUrlError(null);
    }

    handleSave({ url: newUrl });
  };

  return (
    <Stack spacing={2}>
      {props.isEditMode ? (
        <>
          <TextField
            label={t('dtbs.library.detail.content.blockTitle')}
            value={titleValue}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
            fullWidth
          />
          <TextField
            label={t('dtbs.library.detail.content.videoBlock.videoUrl')}
            value={props.block.contentData?.videoUrl ?? ''}
            onChange={handleUrlChange}
            error={!!videoUrlError}
            helperText={videoUrlError || t('dtbs.library.detail.content.videoBlock.videoUrlHint')}
            fullWidth
          />
          <TextField
            label={t('dtbs.library.detail.content.videoBlock.description')}
            value={props.block.contentData?.caption ?? ''}
            onChange={handleDescriptionChange}
            helperText={`${props.block.contentData?.caption?.length ?? 0}/${MAX_CAPTION}`}
            slotProps={{
              formHelperText: { sx: { textAlign: 'right', ml: 0 } },
            }}
          />
        </>
      ) : (
        <>
          {props.block.contentData?.videoUrl ? (
            <BrightcovePlayer videoUrl={props.block.contentData.videoUrl} />
          ) : (
            <Alert severity='warning'>{t('dtbs.library.detail.content.videoBlock.noVideoUrl')}</Alert>
          )}
          {props.block.contentData?.caption && (
            <Box sx={{ mt: 2 }}>
              {parse(props.block.contentData.caption, {
                replace(node: DOMNode, ind: number) {
                  return PolicyContentHelper.handleContent(node, ind);
                },
              })}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};

export default VideoBlock;
